/**
 * AuthService (frontend) — Browser-side client for the /api/auth endpoints.
 *
 * Responsibilities:
 *  - Call the backend auth REST endpoints (register / login / logout / session)
 *  - Persist the session (token + username) in sessionStorage so a page
 *    reload resumes the same login (guests have no session)
 *
 * The backend is authoritative: this service only transports credentials and
 * tokens. It never stores or hashes passwords, and it never derives the
 * player identity locally — the server confirms it via the Authenticated
 * socket message.
 *
 * Testability: `fetch` and the session storage are injectable.
 */

// ─── Types ────────────────────────────────────────────────────────────────

/** Result of an auth operation (register/login). */
export interface AuthResult {
  success: boolean;
  /** Human-readable error when success is false. */
  error?: string;
  /** The user's username (only on success). */
  username?: string;
  /** The session token issued by the server (only on success). */
  token?: string;
  /**
   * The user's daily login streak (consecutive days) as computed by the
   * server for THIS login (only on success; absent from older servers).
   */
  streak?: number;
}

/** A persisted session. */
export interface StoredSession {
  token: string;
  username: string;
}

/** Minimal Storage interface (sessionStorage in the browser, mock in tests). */
export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Options for constructing an AuthService. */
export interface AuthServiceOptions {
  /** Base URL for auth endpoints. Default: '' (same origin). */
  baseUrl?: string;
  /** Fetch implementation override (for tests). */
  fetchImpl?: typeof fetch;
  /** Session storage override (for tests). */
  storage?: SessionStorageLike;
}

// ─── Session Persistence ──────────────────────────────────────────────────

/** sessionStorage key used for the persisted session. */
export const SESSION_STORAGE_KEY = 'project_x_session';

/**
 * Default fetch implementation. Always invokes `fetch` through the global
 * scope so the browser's WebIDL brand check sees `window` as the receiver.
 * (A detached reference stored on an object throws in real browsers but not
 * in Node — which is why this must be done explicitly.)
 */
const defaultGlobalFetch: typeof fetch = (input: RequestInfo | URL, init?: RequestInit) =>
  (globalThis as typeof globalThis).fetch(input, init);

/**
 * Returns the browser sessionStorage, or null when unavailable (tests/SSR).
 */
function defaultStorage(): SessionStorageLike | null {
  try {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage;
    }
  } catch {
    // sessionStorage can throw in some privacy modes — treat as unavailable
  }
  return null;
}

/**
 * Persists a session in storage (no-op when no storage is available).
 */
export function saveSession(
  session: StoredSession,
  storage: SessionStorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage full/unavailable — session simply won't survive reloads
  }
}

/**
 * Loads a persisted session, or null when absent/corrupt.
 */
export function loadSession(
  storage: SessionStorageLike | null = defaultStorage()
): StoredSession | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' && parsed !== null &&
      typeof (parsed as StoredSession).token === 'string' &&
      typeof (parsed as StoredSession).username === 'string'
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Clears the persisted session.
 */
export function clearSession(
  storage: SessionStorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ─── Auth Service ─────────────────────────────────────────────────────────

export class AuthService {
  private readonly _baseUrl: string;
  private readonly _fetch: typeof fetch;
  private readonly _storage: SessionStorageLike | null;

  constructor(options: AuthServiceOptions = {}) {
    this._baseUrl = options.baseUrl ?? '';
    // IMPORTANT: the browser's native `fetch` is WebIDL brand-checked — it
    // must be invoked with `window`/`globalThis` as its receiver. Storing a
    // detached reference (e.g. `x = fetch`) and calling `x(...)` throws
    // "'fetch' called on an object that does not implement interface Window".
    // So the default always calls fetch through the global scope, which is
    // safe both in the browser (receiver = window) and in Node (undici).
    // Injected `fetchImpl` (tests) is a plain function with no brand check.
    this._fetch = options.fetchImpl ?? defaultGlobalFetch;
    this._storage = options.storage ?? defaultStorage();
  }

  /**
   * Registers a new account. On success the server returns a session token
   * (auto-login), which is persisted locally.
   */
  async register(username: string, password: string): Promise<AuthResult> {
    const result = await this._post('/api/auth/register', { username, password });
    if (result.success && result.username && result.token) {
      this.persistSession(result.username, result.token);
    }
    return result;
  }

  /**
   * Logs in with existing credentials. On success the session token is
   * persisted locally.
   */
  async login(username: string, password: string): Promise<AuthResult> {
    const result = await this._post('/api/auth/login', { username, password });
    if (result.success && result.username && result.token) {
      this.persistSession(result.username, result.token);
    }
    return result;
  }

  /**
   * Signs in a CrazyGames player (the /crazygames subpage). The client
   * posts a fresh CrazyGames user token (RS256 JWT from the platform SDK);
   * the server VERIFIES it and maps it to a real user account (created on
   * first sight), returning a normal session token + the server-derived
   * username. On success the session is persisted exactly like a regular
   * login (so a page reload resumes via verifySession without re-posting a
   * token). CrazyGames players are FULLY logged-in users — skins, stats,
   * achievements, toplist and streak all work.
   */
  async crazyGamesLogin(crazyGamesToken: string): Promise<AuthResult> {
    const result = await this._post('/api/auth/crazygames', { token: crazyGamesToken });
    if (result.success && result.username && result.token) {
      this.persistSession(result.username, result.token);
    }
    return result;
  }

  /**
   * Verifies a stored token. Returns the username (and the server-computed
   * daily login streak when the server reports one) when valid, null
   * otherwise. A session resume also advances the streak server-side (it is
   * "the user logged on"), so the returned streak is current.
   */
  async verifySession(token: string): Promise<{ username: string; streak?: number } | null> {
    const res = await this._post<{ success: boolean; username?: string; streak?: number }>('/api/auth/session', { token });
    if (res.success && res.username) {
      // Include the streak only when the server actually sent it — keeps
      // the returned object shape stable for older server responses.
      return res.streak !== undefined
        ? { username: res.username, streak: res.streak }
        : { username: res.username };
    }
    return null;
  }

  /**
   * Claims the 7-day login streak reward.
   *
   * The SERVER is authoritative: it validates the streak (>= 7) and, on
   * success, resets the cycle to day 1 (the response's streak is 1 — the
   * new counter) AND grants one RANDOM skin from the streak reward pool
   * that the user does not already own (`reward`; null when the user owns
   * the whole pool). The response ALWAYS carries the current streak, also
   * when the claim is rejected (stale UI / double click), so the caller
   * can resync its display instead of trusting a stale local value.
   */
  async claimStreak(token: string): Promise<{
    success: boolean;
    /** True only when the server actually performed the claim. */
    claimed?: boolean;
    /** The current streak after the (attempted) claim — always resync to it. */
    streak?: number;
    /**
     * The skin unlocked by the claim (present only on a successful claim;
     * null when the user already owns every reward-pool skin — nothing to
     * grant). Absent on rejected claims.
     */
    reward?: { skinId: string; minionType: string; name: string; imageUrl: string } | null;
    error?: string;
  }> {
    return this._post('/api/auth/claim-streak', { token });
  }

  /**
   * Logs out: invalidates the token server-side and clears local storage.
   */
  async logout(token: string): Promise<void> {
    await this._post('/api/auth/logout', { token });
    clearSession(this._storage);
  }

  /** The currently persisted session (if any). */
  getStoredSession(): StoredSession | null {
    return loadSession(this._storage);
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  private persistSession(username: string, token: string): void {
    saveSession({ token, username }, this._storage);
  }

  private async _post<T = { success: boolean; error?: string; username?: string; token?: string }>(
    path: string,
    body: Record<string, unknown>
  ): Promise<T> {
    // Primary: same origin (works when the page is served by the backend or
    // by Vite with the /api proxy). Fallback: the dev backend directly on
    // port 3001 (works when the page is served from a static preview that
    // does not proxy /api — the backend answers cross-origin via CORS).
    const bases: string[] = [this._baseUrl];
    if (!this._baseUrl && typeof window !== 'undefined') {
      const devBase = devBackendBaseUrl();
      if (devBase) bases.push(devBase);
    }

    const fail = (error: string): T => {
      console.warn(`AuthService: POST ${path} failed: ${error}`);
      return { success: false, error } as T;
    };

    for (let i = 0; i < bases.length; i++) {
      const base = bases[i];
      try {
        const response = await this._fetch(`${base}${path}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        // A 404/405 on the first attempt means this origin does not route
        // /api (no proxy) — try the next base if there is one.
        if (i < bases.length - 1 && (response.status === 404 || response.status === 405)) {
          console.warn(`AuthService: ${base}${path} returned ${response.status} — trying direct backend`);
          continue;
        }

        let data: T;
        try {
          data = (await response.json()) as T;
        } catch {
          // Non-JSON response on the LAST attempt is a hard failure;
          // on earlier attempts keep trying the next base.
          if (i < bases.length - 1) continue;
          return fail('Server returned an invalid response.');
        }
        return { ...data, success: response.ok && Boolean((data as { success?: boolean }).success) };
      } catch (err) {
        // Network-level failure (connection refused/reset, CORS block):
        // try the next base if there is one.
        if (i < bases.length - 1) {
          console.warn(`AuthService: fetch ${base}${path} threw (${String(err)}) — trying direct backend`);
          continue;
        }
        return fail('Could not reach the server. Check your connection.');
      }
    }

    return fail('Could not reach the server. Check your connection.');
  }
}

/**
 * Dev-mode fallback base URL: the backend on port 3001 of the page's host.
 * Only used when the page's own origin cannot be reached for /api (e.g., the
 * page is served by a static preview without the Vite /api proxy).
 */
function devBackendBaseUrl(): string {
  try {
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
    return `${protocol}://${window.location.hostname}:3001`;
  } catch {
    return '';
  }
}
